import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { QuestionTypeSelector } from "./QuestionTypeSelector";
import { DifficultySelector } from "./DifficultySelector";
import { TopicSelector } from "./TopicSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { Quiz } from "@/features/quizzes/types";

export interface QuizEditData {
  questionTypes: string[];
  difficulty: string;
  questionCount: number;
  topics: string[];
  instructions: string;
}

interface QuizEditModalProps {
  open: boolean;
  onClose: () => void;
  quiz: Quiz;
  onSave: (data: QuizEditData) => void;
  isLoading?: boolean;
}

export const QuizEditModal = ({
  open,
  onClose,
  quiz,
  onSave,
  isLoading = false,
}: QuizEditModalProps) => {
  const [questionTypes, setQuestionTypes] = useState<string[]>(
    quiz.question_types || []
  );
  const [difficulty, setDifficulty] = useState<string>(
    quiz.difficulty || "medium"
  );
  const [questionCount, setQuestionCount] = useState<number>(
    quiz.question_count || 10
  );
  const [topics, setTopics] = useState<string[]>(quiz.topics || []);
  const [instructions, setInstructions] = useState<string>(
    quiz.instructions || ""
  );

  const handleSave = () => {
    onSave({
      questionTypes,
      difficulty,
      questionCount,
      topics,
      instructions,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-normal">
            Customize Quiz
          </DialogTitle>
          <DialogDescription>
            Create quiz sets with preferred question types, difficulty, and more.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm mt-4">
          {/* Question Types */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Question Types <span className="text-destructive">*</span>
            </label>
            <QuestionTypeSelector
              value={questionTypes}
              onChange={setQuestionTypes}
            />
          </div>

          {/* Difficulty */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Difficulty <span className="text-destructive">*</span>
            </label>
            <DifficultySelector value={difficulty} onChange={setDifficulty} />
          </div>

          {/* Number of Questions */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Number of Questions
            </label>
            <Input
              type="number"
              min={1}
              max={40}
              value={questionCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              className="rounded-xl"
            />
          </div>

          {/* Topics */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Topics</label>
            <TopicSelector value={topics} onChange={setTopics} />
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <label className="text-sm font-medium">What should the quiz focus on?</label>
            <Textarea
              className="min-h-[80px] rounded-xl"
              placeholder="Focus on the parts that are about…"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || questionTypes.length === 0}>
            {isLoading ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
